import React, { useRef, useState, useEffect, useCallback } from "react";
import { Modal, ColorPicker, message, Spin } from "antd";
import {
  LeftOutlined,
  RightOutlined,
  CloseOutlined,
  CheckOutlined,
  PlusOutlined,
} from "@ant-design/icons";
import { IconEdit, IconRedo, IconScaling, IconUndo } from "../Icon";
import { useUploadFile } from "../../../hooks/file";

interface Props {
  open: boolean;
  image: string;
  fileId?: string;
  userId?: string;
  currentIndex?: number;
  totalCount?: number;
  onClose: () => void;
  onSave: (result: { id: string; url: string; originalName: string }) => void;
  onPrev?: () => void;
  onNext?: () => void;
}

interface Point {
  x: number;
  y: number;
}

interface DrawPath {
  points: Point[];
  color: string;
}

interface CropState {
  x: number;
  y: number;
  width: number;
  height: number;
}

type HistoryAction =
  | { type: "draw"; path: DrawPath }
  | { type: "crop"; cropData: CropState };

const SimpleImageEditor: React.FC<Props> = ({
  open,
  image,
  currentIndex = 1,
  totalCount = 1,
  onClose,
  onSave,
  onPrev,
  onNext,
  fileId,
  userId,
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const imgRef = useRef<HTMLImageElement>(null);

  const [drawing, setDrawing] = useState(false);
  const [color, setColor] = useState("#ffffff");
  const [tool, setTool] = useState<"pen" | "crop">("pen");
  const [imageLoaded, setImageLoaded] = useState(false);
  const [originalImage, setOriginalImage] = useState<HTMLImageElement | null>(
    null,
  );
  const [history, setHistory] = useState<HistoryAction[]>([]);
  const [historyStep, setHistoryStep] = useState(-1);
  const [currentPath, setCurrentPath] = useState<Point[]>([]);

  const [cropStart, setCropStart] = useState<Point | null>(null);
  const [cropEnd, setCropEnd] = useState<Point | null>(null);
  const [isCropping, setIsCropping] = useState(false);
  const [showCropConfirm, setShowCropConfirm] = useState(false);
  const uploader = useUploadFile("file/upload");
  const [imageSrc, setImageSrc] = useState<string>("");
  const [isLoading, setIsLoading] = useState(false);

  const [presetColors] = useState([
    { color: "#ff4444", name: "red" },
    { color: "#000000", name: "black" },
    { color: "#ffffff", name: "white" },
  ]);

  const resetState = useCallback(() => {
    setImageLoaded(false);
    setOriginalImage(null);
    setHistory([]);
    setHistoryStep(-1);
    setCurrentPath([]);
    setCropStart(null);
    setCropEnd(null);
    setIsCropping(false);
    setShowCropConfirm(false);
    setTool("pen");
    setDrawing(false);

    const canvas = canvasRef.current;
    if (canvas) {
      const ctx = canvas.getContext("2d");
      if (ctx) {
        ctx.clearRect(0, 0, canvas.width, canvas.height);
      }
      canvas.width = 0;
      canvas.height = 0;
    }
  }, []);

  const redrawCanvas = async (step: number) => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext("2d");
    if (!canvas || !ctx || !originalImage) return;

    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(originalImage, 0, 0, canvas.width, canvas.height);

    for (let i = 0; i <= step; i++) {
      const action = history[i];
      if (!action) continue;

      if (action.type === "draw") {
        const path = action.path;
        if (path.points.length > 0) {
          ctx.strokeStyle = path.color;
          ctx.lineWidth = 3;
          ctx.lineCap = "round";
          ctx.lineJoin = "round";
          ctx.beginPath();
          ctx.moveTo(path.points[0].x, path.points[0].y);
          for (let j = 1; j < path.points.length; j++) {
            ctx.lineTo(path.points[j].x, path.points[j].y);
          }
          ctx.stroke();
        }
      } else if (action.type === "crop") {
        const crop = action.cropData;
        const tempCanvas = document.createElement("canvas");
        tempCanvas.width = crop.width;
        tempCanvas.height = crop.height;
        const tempCtx = tempCanvas.getContext("2d");
        if (!tempCtx) continue;

        tempCtx.drawImage(
          canvas,
          crop.x,
          crop.y,
          crop.width,
          crop.height,
          0,
          0,
          crop.width,
          crop.height,
        );

        canvas.width = crop.width;
        canvas.height = crop.height;
        ctx.drawImage(tempCanvas, 0, 0);
      }
    }
  };

  const undo = () => {
    if (historyStep >= 0) {
      const newStep = historyStep - 1;
      setHistoryStep(newStep);

      const lastAction = history[historyStep];
      if (lastAction?.type === "crop") {
        resetCanvasSize();
      }

      setTimeout(() => redrawCanvas(newStep), 0);
    }
    setCropStart(null);
    setCropEnd(null);
    setShowCropConfirm(false);
  };

  const redo = () => {
    if (historyStep < history.length - 1) {
      const newStep = historyStep + 1;
      setHistoryStep(newStep);
      redrawCanvas(newStep);
    }
  };

  const resetCanvasSize = () => {
    const canvas = canvasRef.current;
    const img = originalImage;
    if (!canvas || !img) return;

    const maxWidth = 800;
    const maxHeight = 600;
    const scale = Math.min(maxWidth / img.width, maxHeight / img.height, 1);

    canvas.width = img.width * scale;
    canvas.height = img.height * scale;
  };

  const handleImageLoad = () => {
    const canvas = canvasRef.current;
    const img = imgRef.current;

    if (!canvas || !img) {
      console.error("Missing canvas or img");
      return;
    }

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const maxWidth = 800;
    const maxHeight = 600;
    const scale = Math.min(maxWidth / img.width, maxHeight / img.height, 1);

    canvas.width = Math.floor(img.width * scale);
    canvas.height = Math.floor(img.height * scale);

    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

    setOriginalImage(img);
    setImageLoaded(true);
    setIsLoading(false);
    setHistory([]);
    setHistoryStep(-1);
    setCropStart(null);
    setCropEnd(null);
    setShowCropConfirm(false);
  };

  const startDraw = (e: React.MouseEvent) => {
    if (!imageLoaded || tool !== "pen") return;

    const { offsetX, offsetY } = e.nativeEvent;
    setCurrentPath([{ x: offsetX, y: offsetY }]);
    setDrawing(true);

    const ctx = canvasRef.current?.getContext("2d");
    if (!ctx) return;
    ctx.strokeStyle = color;
    ctx.lineWidth = 3;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.beginPath();
    ctx.moveTo(offsetX, offsetY);
  };

  const draw = (e: React.MouseEvent) => {
    if (!drawing || tool !== "pen") return;

    const { offsetX, offsetY } = e.nativeEvent;
    setCurrentPath((prev) => [...prev, { x: offsetX, y: offsetY }]);

    const ctx = canvasRef.current?.getContext("2d");
    if (!ctx) return;
    ctx.lineTo(offsetX, offsetY);
    ctx.stroke();
  };

  const stopDraw = () => {
    if (drawing && currentPath.length > 0) {
      const newPath: DrawPath = { points: currentPath, color };
      const newHistory = history.slice(0, historyStep + 1);
      newHistory.push({ type: "draw", path: newPath });

      setHistory(newHistory);
      setHistoryStep(newHistory.length - 1);
      setDrawing(false);
      setCurrentPath([]);
    }
  };

  const startCrop = (e: React.MouseEvent) => {
    if (!imageLoaded || tool !== "crop") return;

    const { offsetX, offsetY } = e.nativeEvent;
    setCropStart({ x: offsetX, y: offsetY });
    setCropEnd({ x: offsetX, y: offsetY });
    setIsCropping(true);
    setShowCropConfirm(false);
  };

  const updateCrop = (e: React.MouseEvent) => {
    if (!isCropping || tool !== "crop") return;

    const { offsetX, offsetY } = e.nativeEvent;
    setCropEnd({ x: offsetX, y: offsetY });
  };

  const stopCrop = () => {
    if (isCropping) {
      setIsCropping(false);
      if (cropStart && cropEnd) {
        const width = Math.abs(cropEnd.x - cropStart.x);
        const height = Math.abs(cropEnd.y - cropStart.y);
        if (width > 10 && height > 10) {
          setShowCropConfirm(true);
        } else {
          setCropStart(null);
          setCropEnd(null);
        }
      }
    }
  };

  const applyCrop = () => {
    if (!cropStart || !cropEnd || !canvasRef.current) return;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const x = Math.min(cropStart.x, cropEnd.x);
    const y = Math.min(cropStart.y, cropEnd.y);
    const width = Math.abs(cropEnd.x - cropStart.x);
    const height = Math.abs(cropEnd.y - cropStart.y);

    const tempCanvas = document.createElement("canvas");
    tempCanvas.width = width;
    tempCanvas.height = height;
    const tempCtx = tempCanvas.getContext("2d");
    if (!tempCtx) return;

    tempCtx.drawImage(canvas, x, y, width, height, 0, 0, width, height);

    canvas.width = width;
    canvas.height = height;

    ctx.drawImage(tempCanvas, 0, 0);

    const cropData: CropState = { x, y, width, height };
    const newHistory = history.slice(0, historyStep + 1);
    newHistory.push({ type: "crop", cropData });

    setHistory(newHistory);
    setHistoryStep(newHistory.length - 1);

    setCropStart(null);
    setCropEnd(null);
    setShowCropConfirm(false);
  };

  const cancelCrop = () => {
    setCropStart(null);
    setCropEnd(null);
    setShowCropConfirm(false);
    setIsCropping(false);
  };

  const handleColorChange = (newColor: string) => {
    setColor(newColor);
  };

  const canUndo = historyStep >= 0;
  const canRedo = historyStep < history.length - 1;

  const getCropRect = () => {
    if (!cropStart || !cropEnd) return null;
    const x = Math.min(cropStart.x, cropEnd.x);
    const y = Math.min(cropStart.y, cropEnd.y);
    const width = Math.abs(cropEnd.x - cropStart.x);
    const height = Math.abs(cropEnd.y - cropStart.y);
    return { x, y, width, height };
  };

  const cropRect = getCropRect();

  useEffect(() => {
    if (!open) return;

    resetState();
    setIsLoading(true);
    setImageSrc("");

    if (image.startsWith("data:")) {
      setImageSrc(image);
      return;
    }

    const loadViaProxy = async () => {
      try {
        const proxyUrl = `${process.env.REACT_APP_API}file/${fileId}/${userId}`;

        const response = await fetch(proxyUrl, {
          headers: {
            Accept: "*/*",
          },
        });

        if (!response.ok) throw new Error(`HTTP ${response.status}`);

        const blob = await response.blob();
        const reader = new FileReader();

        reader.onload = (e) => {
          const base64 = e.target?.result as string;
          setImageSrc(base64);
        };

        reader.readAsDataURL(blob);
      } catch (error) {
        console.error("Proxy failed:", error);
        setImageSrc(image);
      }
    };

    loadViaProxy();
  }, [image, fileId, userId, open, resetState]);

  const uploaderRef = useRef(uploader);
  uploaderRef.current = uploader;

  const handleApply = async () => {
    try {
      message.loading("Saving...", 0);

      const canvas = canvasRef.current;
      if (!canvas) throw new Error("Canvas not found");
      if (!imageLoaded) throw new Error("Image not loaded");

      const blob = await new Promise<Blob>((resolve, reject) => {
        canvas.toBlob((b) => {
          if (b) resolve(b);
          else reject(new Error("Canvas toBlob failed"));
        }, "image/png");
      });

      const file = new File([blob], `edited-${Date.now()}.png`, {
        type: "image/png",
      });

      const formData = new FormData();
      formData.append("file", file);

      if (!uploaderRef.current?.fetch) {
        throw new Error("Uploader not available");
      }

      const res = await uploaderRef.current.fetch(formData);

      if (!res?.id) throw new Error("Upload failed - no id");

      onSave({
        id: res.id,
        url: res.location || res.url || "",
        originalName: file.name,
      });

      message.destroy();
      message.success("Saved!");
      onClose();
    } catch (error) {
      message.destroy();
      console.error("Save error:", error);
      message.error(error instanceof Error ? error.message : "Failed to save");
    }
  };

  const handleMouseDown = (e: React.MouseEvent) => {
    if (tool === "pen") {
      startDraw(e);
    } else if (tool === "crop") {
      startCrop(e);
    }
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (tool === "pen") {
      draw(e);
    } else if (tool === "crop") {
      updateCrop(e);
    }
  };

  const handleMouseUp = () => {
    if (tool === "pen") {
      stopDraw();
    } else if (tool === "crop") {
      stopCrop();
    }
  };

  const handlePrev = () => {
    resetState();
    onPrev?.();
  };

  const handleNext = () => {
    resetState();
    onNext?.();
  };

  return (
    <Modal
      open={open}
      onCancel={onClose}
      forceRender={true}
      footer={null}
      width={900}
      style={{ top: 0, padding: 0 }}
      className="customModal"
      closable={false}
      styles={{
        body: { padding: 0, height: "100vh" },
        mask: { backgroundColor: "rgba(0, 0, 0, 0.6)" },
      }}
    >
      <div
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: "transparent",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {/* Top right buttons */}
        <div
          style={{
            position: "absolute",
            top: 20,
            right: 20,
            display: "flex",
            gap: 10,
            zIndex: 100,
          }}
        >
          <button
            onClick={handleApply}
            disabled={!imageLoaded}
            style={{
              backgroundColor: "#9A292B",
              color: "white",
              border: "none",
              padding: "5px 16px",
              borderRadius: 6,
              cursor: imageLoaded ? "pointer" : "not-allowed",
              fontSize: 14,
              fontWeight: 500,
              opacity: imageLoaded ? 1 : 0.5,
            }}
          >
            Apply
          </button>
          <button
            onClick={onClose}
            style={{
              backgroundColor: "#0000001A",
              color: "white",
              border: "none",
              width: 36,
              height: 36,
              borderRadius: "50%",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <CloseOutlined />
          </button>
        </div>

        {/* Navigation arrows */}
        <button
          onClick={handlePrev}
          disabled={isLoading || totalCount <= 1}
          style={{
            position: "absolute",
            left: 20,
            top: "50%",
            transform: "translateY(-50%)",
            backgroundColor: isLoading ? "#0000000D" : "#0000001A",
            color: "white",
            border: "none",
            width: 40,
            height: 40,
            borderRadius: "50%",
            cursor: isLoading ? "not-allowed" : "pointer",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 10,
            opacity: isLoading ? 0.5 : 1,
          }}
        >
          <LeftOutlined />
        </button>

        <button
          onClick={handleNext}
          disabled={isLoading || totalCount <= 1}
          style={{
            position: "absolute",
            right: 20,
            top: "50%",
            transform: "translateY(-50%)",
            backgroundColor: isLoading ? "#0000000D" : "#0000001A",
            color: "white",
            border: "none",
            width: 40,
            height: 40,
            borderRadius: "50%",
            cursor: isLoading ? "not-allowed" : "pointer",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 10,
            opacity: isLoading ? 0.5 : 1,
          }}
        >
          <RightOutlined />
        </button>

        {/* Image container */}
        <div
          style={{
            position: "relative",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          <div
            style={{
              backgroundColor: "white",
              overflow: "hidden",
              boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
              position: "relative",
              minWidth: 200,
              minHeight: 200,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            {/* Loading spinner */}
            {isLoading && (
              <div style={{ position: "absolute", zIndex: 5 }}>
                <Spin size="large" />
              </div>
            )}

            <canvas
              ref={canvasRef}
              onMouseDown={handleMouseDown}
              onMouseMove={handleMouseMove}
              onMouseUp={handleMouseUp}
              onMouseLeave={handleMouseUp}
              style={{
                display: isLoading ? "none" : "block",
                cursor:
                  tool === "pen"
                    ? "crosshair"
                    : tool === "crop"
                      ? "crosshair"
                      : "default",
                maxWidth: "80vw",
                maxHeight: "70vh",
              }}
            />

            {tool === "crop" && cropRect && !isLoading && (
              <>
                <div
                  style={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    pointerEvents: "none",
                  }}
                >
                  <div
                    style={{
                      position: "absolute",
                      top: 0,
                      left: 0,
                      right: 0,
                      height: cropRect.y,
                      backgroundColor: "rgba(0, 0, 0, 0.5)",
                    }}
                  />
                  <div
                    style={{
                      position: "absolute",
                      bottom: 0,
                      left: 0,
                      right: 0,
                      height: `calc(100% - ${cropRect.y + cropRect.height}px)`,
                      backgroundColor: "rgba(0, 0, 0, 0.5)",
                    }}
                  />
                  <div
                    style={{
                      position: "absolute",
                      top: cropRect.y,
                      left: 0,
                      width: cropRect.x,
                      height: cropRect.height,
                      backgroundColor: "rgba(0, 0, 0, 0.5)",
                    }}
                  />
                  <div
                    style={{
                      position: "absolute",
                      top: cropRect.y,
                      right: 0,
                      width: `calc(100% - ${cropRect.x + cropRect.width}px)`,
                      height: cropRect.height,
                      backgroundColor: "rgba(0, 0, 0, 0.5)",
                    }}
                  />
                </div>
                <div
                  style={{
                    position: "absolute",
                    left: cropRect.x,
                    top: cropRect.y,
                    width: cropRect.width,
                    height: cropRect.height,
                    pointerEvents: "none",
                    boxSizing: "border-box",
                  }}
                >
                  <div
                    style={{
                      position: "absolute",
                      top: 0,
                      left: 0,
                      width: 20,
                      height: 3,
                      backgroundColor: "white",
                    }}
                  />
                  <div
                    style={{
                      position: "absolute",
                      top: 0,
                      left: 0,
                      width: 3,
                      height: 20,
                      backgroundColor: "white",
                    }}
                  />
                  <div
                    style={{
                      position: "absolute",
                      top: 0,
                      right: 0,
                      width: 20,
                      height: 3,
                      backgroundColor: "white",
                    }}
                  />
                  <div
                    style={{
                      position: "absolute",
                      top: 0,
                      right: 0,
                      width: 3,
                      height: 20,
                      backgroundColor: "white",
                    }}
                  />
                  <div
                    style={{
                      position: "absolute",
                      bottom: 0,
                      left: 0,
                      width: 20,
                      height: 3,
                      backgroundColor: "white",
                    }}
                  />
                  <div
                    style={{
                      position: "absolute",
                      bottom: 0,
                      left: 0,
                      width: 3,
                      height: 20,
                      backgroundColor: "white",
                    }}
                  />
                  <div
                    style={{
                      position: "absolute",
                      bottom: 0,
                      right: 0,
                      width: 20,
                      height: 3,
                      backgroundColor: "white",
                    }}
                  />
                  <div
                    style={{
                      position: "absolute",
                      bottom: 0,
                      right: 0,
                      width: 3,
                      height: 20,
                      backgroundColor: "white",
                    }}
                  />
                </div>
              </>
            )}
          </div>

          {/* Hidden image for loading */}
          {imageSrc && (
            <img
              key={imageSrc}
              ref={imgRef}
              src={imageSrc}
              onLoad={handleImageLoad}
              style={{ display: "none" }}
            />
          )}

          {/* Counter */}
          <div
            style={{
              color: "white",
              marginTop: 15,
              fontSize: 14,
              fontWeight: 500,
              opacity: isLoading ? 0.5 : 1,
            }}
          >
            {currentIndex} / {totalCount}
          </div>

          {/* Toolbar */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              height: 46,
              marginTop: 35,
              backgroundColor: "#0000001A",
              padding: "14px 34px",
              borderRadius: 23,
              position: "relative",
              opacity: isLoading ? 0.5 : 1,
              pointerEvents: isLoading ? "none" : "auto",
            }}
          >
            {/* Crop tool */}
            <button
              onClick={() => {
                setTool("crop");
                setCropStart(null);
                setCropEnd(null);
                setShowCropConfirm(false);
              }}
              style={{
                backgroundColor: tool === "crop" ? "#787878" : "transparent",
                border: "none",
                color: "white",
                width: 30,
                height: 30,
                borderRadius: 4,
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <IconScaling />
            </button>

            {/* Pen tool */}
            <button
              onClick={() => {
                setTool("pen");
                setCropStart(null);
                setCropEnd(null);
                setShowCropConfirm(false);
              }}
              style={{
                backgroundColor: tool === "pen" ? "#787878" : "transparent",
                border: "none",
                color: "white",
                width: 36,
                height: 36,
                borderRadius: 4,
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <IconEdit />
            </button>

            {/* Preset colors */}
            {tool === "pen" && (
              <div
                style={{
                  display: "flex",
                  gap: 12,
                  margin: "0 36px",
                  alignItems: "center",
                }}
              >
                {presetColors.map((c) => (
                  <button
                    key={c.name}
                    onClick={() => handleColorChange(c.color)}
                    style={{
                      width: 24,
                      height: 24,
                      borderRadius: "50%",
                      backgroundColor: c.color,
                      border:
                        color === c.color
                          ? "2px solid white"
                          : "2px solid transparent",
                      cursor: "pointer",
                      padding: 0,
                    }}
                  />
                ))}

                <div style={{ position: "relative" }}>
                  <ColorPicker
                    value={color}
                    onChange={(colorObj) =>
                      handleColorChange(colorObj.toHexString())
                    }
                    showText={false}
                    presets={[
                      {
                        label: "Current",
                        colors: presetColors.map((p) => p.color),
                      },
                    ]}
                  >
                    <button
                      style={{
                        width: 24,
                        height: 24,
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        borderRadius: "50%",
                        backgroundColor: color,
                        border: "2px solid white",
                        cursor: "pointer",
                      }}
                    >
                      <PlusOutlined />
                    </button>
                  </ColorPicker>
                </div>
              </div>
            )}

            {/* Undo */}
            <button
              onClick={undo}
              disabled={!canUndo || isLoading}
              style={{
                backgroundColor: "transparent",
                border: "none",
                color: canUndo && !isLoading ? "white" : "#9ca3af",
                width: 36,
                height: 36,
                borderRadius: 4,
                cursor: canUndo && !isLoading ? "pointer" : "not-allowed",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <IconUndo />
            </button>

            {/* Redo */}
            <button
              onClick={redo}
              disabled={!canRedo || isLoading}
              style={{
                backgroundColor: "transparent",
                border: "none",
                color: canRedo && !isLoading ? "white" : "#9ca3af",
                width: 36,
                height: 36,
                borderRadius: 4,
                cursor: canRedo && !isLoading ? "pointer" : "not-allowed",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <IconRedo />
            </button>
          </div>

          {tool === "crop" && showCropConfirm && !isLoading && (
            <div
              style={{
                display: "flex",
                gap: 8,
                marginTop: 10,
              }}
            >
              <button
                onClick={applyCrop}
                style={{
                  backgroundColor: "#9A292B",
                  color: "white",
                  border: "none",
                  padding: "5px 16px",
                  borderRadius: 6,
                  cursor: "pointer",
                  fontSize: 13,
                  fontWeight: 500,
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                }}
              >
                <CheckOutlined />
                Apply Crop
              </button>
              <button
                onClick={cancelCrop}
                style={{
                  backgroundColor: "#4b5563",
                  color: "white",
                  border: "none",
                  padding: "6px 16px",
                  borderRadius: 4,
                  cursor: "pointer",
                  fontSize: 13,
                  fontWeight: 500,
                }}
              >
                Cancel
              </button>
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
};

export default SimpleImageEditor;
